import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CreditCard, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BRANDS = { visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover", diners: "Diners", jcb: "JCB", unionpay: "UnionPay", cartes_bancaires: "Cartes Bancaires" };
export const brandLabel = (b) => BRANDS[(b || "").toLowerCase()] || (b ? b.charAt(0).toUpperCase() + b.slice(1) : "");

// Paramètres › « Mes cartes » : les cartes mémorisées lors d'un soutien (chez Stripe) — voir et retirer sans passer par un don.
export const MySavedCards = () => {
  const { t } = useApp();
  const [cards, setCards] = useState(null);
  const [removing, setRemoving] = useState(null); // carte en attente de confirmation
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/payments/cards").then(({ data }) => setCards(data.cards || [])).catch(() => setCards([]));
  }, []);
  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await api.delete(`/payments/cards/${removing.id}`);
      setCards((c) => (c || []).filter((x) => x.id !== removing.id));
      toast.success(t("cards.removed", "Carte retirée"));
      setRemoving(null);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    setBusy(false);
  };
  return (
    <div className="yalola-card p-5" data-testid="settings-cards-card">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard size={18} style={{ color: "var(--yalola-primary)" }} />
        <div className="font-bold font-display text-lg">{t("cards.title", "Mes cartes")}</div>
      </div>
      <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
        {t("cards.desc", "Les cartes que tu as choisi de mémoriser pour soutenir en un tap. Elles sont gardées par Stripe, jamais par Yalola.")}
      </p>
      {cards === null && <div className="flex items-center gap-2 text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="cards-loading"><Loader2 size={16} className="animate-spin" />{t("common.loading", "Chargement…")}</div>}
      {cards && cards.length === 0 && <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="cards-empty">{t("cards.empty", "Aucune carte mémorisée. Coche « Enregistrer ma carte » lors d'un prochain soutien.")}</p>}
      {cards && cards.length > 0 && (
        <ul className="space-y-2" data-testid="cards-list">
          {cards.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2" style={{ borderColor: "var(--yalola-border)" }} data-testid={`card-row-${c.last4}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="h-8 w-12 rounded-md flex items-center justify-center text-[10px] font-bold uppercase shrink-0" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}>{brandLabel(c.brand).slice(0, 4)}</span>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{brandLabel(c.brand)} •••• {c.last4}</div>
                  {c.exp_month && <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("cards.expires", "expire {mm}/{yy}").replace("{mm}", String(c.exp_month).padStart(2, "0")).replace("{yy}", String(c.exp_year).slice(-2))}</div>}
                </div>
              </div>
              <Button variant="outline" size="sm" className="rounded-full shrink-0" onClick={() => setRemoving(c)} data-testid={`card-remove-${c.last4}`}>
                <Trash2 size={14} className="mr-1" />{t("cards.remove", "Retirer")}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <AlertDialog open={!!removing} onOpenChange={(v) => { if (!v && !busy) setRemoving(null); }}>
        <AlertDialogContent data-testid="card-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cards.remove_confirm", "Retirer cette carte ?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {removing ? `${brandLabel(removing.brand)} •••• ${removing.last4} — ` : ""}{t("cards.remove_confirm_desc", "Elle ne sera plus proposée lors de tes soutiens. Tu pourras en mémoriser une nouvelle à tout moment.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="card-remove-cancel">{t("common.cancel", "Annuler")}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); remove(); }} disabled={busy} data-testid="card-remove-confirm">
              {busy ? t("off.processing", "Traitement...") : t("cards.remove", "Retirer")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
